import axios from 'axios';
import dayjs from 'dayjs';
import { globalConfig } from '../configuration/config';
import {
  TRANSFORMATION_PROFILES,
  buildTransformationSettings,
  createTransformationFormDefaults,
} from './converter-transformation-settings';

const VARIANT_OPTIMIZATION_POLL_INTERVAL_MS = 2000;
const VARIANT_OPTIMIZATION_POLL_FAILURE_LIMIT = 3;
const VARIANT_OPTIMIZATION_RESULT_NAME_MAX_LENGTH = 512;
const GENERATED_VARIANT_OPTIMIZATION_SUFFIX_PATTERN =
  / — (?:оптимизировано \d{2}\.\d{2}\.\d{4}(?: \d{2}:\d{2}|, \d{2}:\d{2}:\d{2})|опт \d{2}\.\d{2}\.\d{4}, \d{2}:\d{2}(?::\d{2})?)$/u;
const DEFAULT_S123_TRANSFORMATION_SETTINGS = buildTransformationSettings({
  profile: TRANSFORMATION_PROFILES.S123_OPTIMIZATION,
  values: createTransformationFormDefaults(
    TRANSFORMATION_PROFILES.S123_OPTIMIZATION,
  ),
});

const TERMINAL_JOB_STATUSES = new Set([
  'done',
  'failed',
  'publish_unknown',
]);

function normalizeText(value) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : undefined;
}

function getConverterBaseUrl() {
  const converterUrl = normalizeText(
    globalConfig.config.common.glbConverter,
  );

  if (converterUrl === undefined) {
    throw new Error('GLB converter URL is not configured.');
  }

  return converterUrl.replace(/\/+$/, '');
}

function getOidcStorageKey() {
  return (
    `oidc.user:${globalConfig.config.auth.authority}:` +
    globalConfig.config.auth.client_id
  );
}

function readVariantOptimizationAccessToken(
  storage = window.localStorage,
) {
  const rawUser = storage.getItem(getOidcStorageKey());

  if (typeof rawUser !== 'string' || rawUser.trim().length === 0) {
    throw new Error('Authentication access token is unavailable.');
  }

  let storedUser;

  try {
    storedUser = JSON.parse(rawUser);
  } catch (error) {
    throw new Error('Authentication data is invalid.');
  }

  const accessToken = normalizeText(storedUser?.access_token);

  if (accessToken === undefined) {
    throw new Error('Authentication access token is unavailable.');
  }

  return accessToken;
}

export function hasVariantOptimizationAccessToken(
  storage = window.localStorage,
) {
  try {
    readVariantOptimizationAccessToken(storage);
    return true;
  } catch (error) {
    return false;
  }
}

export function normalizeVariantOptimizationResultName(value) {
  if (typeof value !== 'string') {
    throw new Error(
      'Variant optimization result name must contain 1-512 characters.',
    );
  }

  const normalizedValue = value.trim();

  if (
    normalizedValue.length === 0 ||
    normalizedValue.length > VARIANT_OPTIMIZATION_RESULT_NAME_MAX_LENGTH ||
    normalizedValue.includes('\0')
  ) {
    throw new Error(
      'Variant optimization result name must contain 1-512 characters.',
    );
  }

  return normalizedValue;
}

export function formatVariantOptimizationResultName({
  date = new Date(),
  sourceName,
}) {
  const normalizedSourceName = normalizeText(sourceName)?.replace(/\0/g, '').trim();

  if (!normalizedSourceName) {
    throw new Error('Source variant name is unavailable.');
  }

  let sourceNameWithoutGeneratedSuffix = normalizedSourceName;

  while (
    GENERATED_VARIANT_OPTIMIZATION_SUFFIX_PATTERN.test(
      sourceNameWithoutGeneratedSuffix,
    )
  ) {
    sourceNameWithoutGeneratedSuffix = sourceNameWithoutGeneratedSuffix.replace(
      GENERATED_VARIANT_OPTIMIZATION_SUFFIX_PATTERN,
      '',
    );
  }

  const suffix = ` — опт ${dayjs(date).format(
    'DD.MM.YYYY, HH:mm',
  )}`;
  const sourceNameLength = VARIANT_OPTIMIZATION_RESULT_NAME_MAX_LENGTH - suffix.length;

  return normalizeVariantOptimizationResultName(
    `${sourceNameWithoutGeneratedSuffix.slice(0, sourceNameLength)}${suffix}`,
  );
}

function waitForNextPoll(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', abortHandler);
      resolve();
    }, milliseconds);

    function abortHandler() {
      window.clearTimeout(timeoutId);
      signal.removeEventListener('abort', abortHandler);
      reject(new DOMException('Aborted', 'AbortError'));
    }

    signal.addEventListener('abort', abortHandler, { once: true });
  });
}

export function isVariantOptimizationAbortError(error) {
  return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

export async function pollVariantOptimizationJob({
  jobId,
  onRetryableError,
  onStatusChange,
  signal,
}) {
  const normalizedJobId = normalizeText(jobId);

  if (normalizedJobId === undefined) {
    throw new Error('The optimization job id is unavailable.');
  }

  const converterBaseUrl = getConverterBaseUrl();
  let consecutiveFailures = 0;

  while (true) {
    try {
      const statusResponse = await axios.get(
        `${converterBaseUrl}/jobs/${normalizedJobId}`,
        {
          headers: {
            Authorization: `Bearer ${readVariantOptimizationAccessToken()}`,
          },
          signal,
        },
      );
      const jobStatus = statusResponse.data;

      consecutiveFailures = 0;
      onStatusChange?.(jobStatus);

      if (TERMINAL_JOB_STATUSES.has(jobStatus?.status)) {
        return jobStatus;
      }

      await waitForNextPoll(
        VARIANT_OPTIMIZATION_POLL_INTERVAL_MS,
        signal,
      );
    } catch (error) {
      if (isVariantOptimizationAbortError(error)) {
        throw error;
      }

      consecutiveFailures += 1;

      onRetryableError?.({
        attempt: consecutiveFailures,
        error,
      });

      if (consecutiveFailures >= VARIANT_OPTIMIZATION_POLL_FAILURE_LIMIT) {
        throw error;
      }

      await waitForNextPoll(
        VARIANT_OPTIMIZATION_POLL_INTERVAL_MS,
        signal,
      );
    }
  }
}

export async function runVariantOptimization({
  onJobCreated,
  onRetryableError,
  onStatusChange,
  optimizationSettings = DEFAULT_S123_TRANSFORMATION_SETTINGS,
  projectGuid,
  resultName,
  signal,
  sourceCalculationGuid,
}) {
  const normalizedResultName =
    normalizeVariantOptimizationResultName(resultName);
  const converterBaseUrl = getConverterBaseUrl();
  const createResponse = await axios.post(
    `${converterBaseUrl}/variant-optimization-jobs`,
    {
      optimizationSettings,
      projectGuid,
      resultName: normalizedResultName,
      sourceCalculationGuid,
    },
    {
      headers: {
        Authorization: `Bearer ${readVariantOptimizationAccessToken()}`,
        'Content-Type': 'application/json',
      },
      signal,
    },
  );
  const jobId = normalizeText(createResponse.data?.jobId);

  if (jobId === undefined) {
    throw new Error('The optimization server did not return a job id.');
  }

  onJobCreated(createResponse.data);

  return pollVariantOptimizationJob({
    jobId,
    onRetryableError,
    onStatusChange,
    signal,
  });
}

export function extractVariantOptimizationResultCalculationGuid(jobStatus) {
  const calculationGuid = normalizeText(
    jobStatus?.publish?.legacyCalculationGuid ??
      jobStatus?.publishState?.legacyCalculationGuid,
  );

  if (jobStatus?.status !== 'done' || calculationGuid === undefined) {
    throw new Error(
      'The optimization server did not return the published variant.',
    );
  }

  return calculationGuid;
}

export function buildVariantOptimizationResultRoute({
  jobStatus,
  projectGuid,
}) {
  const calculationGuid = extractVariantOptimizationResultCalculationGuid(
    jobStatus,
  );

  return (
    `/app?pr=${encodeURIComponent(projectGuid)}` +
    `&variant=${encodeURIComponent(calculationGuid)}`
  );
}

export function formatVariantOptimizationErrorReport({
  error,
  jobId,
  jobStatus,
  projectGuid,
  sourceCalculationGuid,
}) {
  const report = {
    createdAt: new Date().toISOString(),
    jobId: jobId ?? jobStatus?.id ?? null,
    projectGuid,
    sourceCalculationGuid,
  };

  if (jobStatus !== undefined) {
    report.serverResponse = jobStatus;
  }

  if (error !== undefined) {
    report.clientError = {
      code: error?.code,
      message: error?.message ?? String(error),
      name: error?.name,
      response: error?.response
        ? {
            data: error.response.data,
            status: error.response.status,
            statusText: error.response.statusText,
          }
        : undefined,
      stack: error?.stack,
    };
  }

  return JSON.stringify(report, null, 2);
}
